import { zodResolver } from "@hookform/resolvers/zod";
import {
  ProjectsValidator,
  PROJECT_SUMMARY_MAX,
} from "Validators/Common/Project";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { HTTPErrorCallback } from "Helpers/Error";
import {
  currencyMask,
  defaultCurrencyMask,
  parseLocaleNumber,
  transformIntoArrayOptions,
} from "Helpers/Format";
import { RELATED_AREA_OPTIONS, relatedAreasToOptions } from "Helpers/Project";
import { missingCityMessage } from "Helpers/RealizationAddress";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import FormWrapper from "Components/Common/FormWrapper";
import Button from "Components/Global/Buttons";
import CharCounter from "Components/Global/Form/CharCounter";
import Input from "Components/Global/Form/Input";
import Select from "Components/Global/Form/Select";
import GetIcon from "Components/Global/Icons";

import Checkboxes from "../../Components/Checkboxes";
import Commitments from "../../Components/Commitments";
import DateInput from "../../Components/DateInput";
import DefaultForm from "../../Components/DefaultForm";
import ProjectModal from "../../Components/Modal";
import RealizationAddress from "../../Components/RealizationAddress";
import * as S from "./styles";

import { useTheme } from "styled-components";

const FirstForm = ({
  setStep,
  projectId,
  setProjectId,
  projectData,
  setProjectData,
  disabled,
  isDownload,
}) => {
  const theme = useTheme();

  const { api } = UseServices();
  const { setLoading } = UseGlobalLoading();
  const { showNotification } = UseNotification();

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [selectedCategories, setSelectedCategories] = useState(
    projectData?.selectedCategories || [],
  );

  const [commitments, setCommitments] = useState(
    projectData?.project_commitments || [],
  );
  const [ageRange, setAgeRange] = useState(
    projectData?.project_age_range || [],
  );

  const [totalAuthorizedValue, setTotalAuthorizedValue] = useState(
    projectData?.total_authorized_value
      ? defaultCurrencyMask(projectData?.total_authorized_value, true)
      : null,
  );

  const [captureBalance, setCaptureBalance] = useState(
    projectData?.capture_balance
      ? defaultCurrencyMask(projectData?.capture_balance, true)
      : null,
  );

  const [selectedRelatedAreas, setSelectedRelatedAreas] = useState(
    relatedAreasToOptions(projectData?.related_areas),
  );

  const [realizationAddress, setRealizationAddress] = useState({});
  const [realizationAddressModalOpen, setRealizationAddressModalOpen] =
    useState(false);
  const [currentUf, setCurrentUf] = useState(null);

  const parseJsonField = (field) => {
    try {
      return JSON.parse(field);
    } catch (error) {
      return field;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 1200);
    getCategories();

    if (projectData?.realization_address)
      setRealizationAddress(parseJsonField(projectData?.realization_address));
  }, []);

  const formSchema = z.object({
    responsible_name: ProjectsValidator.responsible_name,
    email: ProjectsValidator.email,
    role: ProjectsValidator.role,
    address: ProjectsValidator.address,
    neighbourhood: ProjectsValidator.neighbourhood,
    city: ProjectsValidator.city,
    state: ProjectsValidator.state,
    cep: ProjectsValidator.cep,
    phone: ProjectsValidator.phone,
    organization_type: ProjectsValidator.organization_type,
    other_organization_type: ProjectsValidator.other_organization_type,
    organization_area: ProjectsValidator.organization_area,
    institution_history: ProjectsValidator.institution_history,
    project_name: ProjectsValidator.project_name,
    project_summary: ProjectsValidator.project_summary,
    main_goal: ProjectsValidator.main_goal,
    project_start_at: ProjectsValidator.project_start_at,
    project_finish_at: ProjectsValidator.project_finish_at,
    project_direct_public_number:
      ProjectsValidator.project_direct_public_number,
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: projectData,
    mode: "onChange",
  });
  const { register, watch, setValue } = methods;

  const getCategories = async () => {
    try {
      const response = await api.get("/projects/get-project-categories");

      const options = transformIntoArrayOptions(
        response.data,
        "id",
        "display_name",
      );

      setCategoryOptions(options);

      const categories = projectData?.categories || [];

      if (categories.length) {
        const selectedCategoriesOptions = options.filter((option) =>
          categories.includes(option.value),
        );

        setSelectedCategories(selectedCategoriesOptions);
      }
    } catch (error) {
      HTTPErrorCallback(error);
    }
  };

  const submit = async (data) => {
    if (disabled) {
      setProjectData({ ...projectData, ...data });
      return setStep(1);
    }

    if (realizationAddress && Object.keys(realizationAddress).length === 0) {
      showNotification(
        "danger",
        "Atenção!",
        "Você precisa selecionar pelo menos um local de realização!",
      );
      return;
    }

    // Cidade obrigatória em toda UF do território (o backend recusa também).
    const missingCity = missingCityMessage(realizationAddress);

    if (missingCity) {
      showNotification("danger", "Atenção!", missingCity);
      return;
    }

    if (!captureBalance) {
      showNotification(
        "danger",
        "Atenção!",
        "Informe o saldo disponível a captar.",
      );
      return;
    }

    if (!selectedRelatedAreas.length) {
      showNotification(
        "danger",
        "Atenção!",
        "Selecione pelo menos uma área relacionada.",
      );
      return;
    }

    try {
      setLoading(true);

      data.step = 1;
      data.project_id = projectId;

      data.project_modality = selectedCategories
        .map((category) => category.label)
        .join(", ");

      data.categories = selectedCategories.map((category) => category.value);
      data.selectedCategories = selectedCategories;

      if (data.other_organization_type) {
        data.actual_organization_type = data.organization_type;
        data.organization_type = data.other_organization_type;
      }

      data.project_commitments = commitments;
      data.project_age_range = ageRange;

      data.realization_address = JSON.stringify(realizationAddress);

      data.total_authorized_value = parseLocaleNumber(totalAuthorizedValue);

      data.capture_balance = parseLocaleNumber(captureBalance);

      data.related_areas = selectedRelatedAreas.map((area) => area.value);

      data.is_update = true;

      const response = await api.post("/projects/submit-project", data);

      if (!projectId) setProjectId(response.data.project_id);

      setProjectData({
        ...projectData,
        ...data,
        organization_type: data.actual_organization_type,
        total_authorized_value: totalAuthorizedValue,
        capture_balance: captureBalance,
        realizationAddress,
      });

      setStep(1);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const submitRealizationAddress = (new_cities) => {
    const uf_cities = [
      ...new Set([
        ...(realizationAddress[currentUf.label] || []),
        ...(new_cities || []),
      ]),
    ];

    setRealizationAddress({
      ...realizationAddress,
      [currentUf.label]: uf_cities,
    });

    setCurrentUf(null);
    setRealizationAddressModalOpen(false);
  };

  const organizationTypeOptions = [
    {
      value: "Pessoa física",
      label: "Pessoa física",
    },
    {
      value: "Pessoa Jurídica Com Fins Lucrativos",
      label: "Pessoa Jurídica Com Fins Lucrativos",
    },
    {
      value: "Pessoa Jurídica Sem Fins Lucrativos",
      label: "Pessoa Jurídica Sem Fins Lucrativos",
    },
    {
      value: "Organização Não Governamental (ONG)",
      label: "Organização Não Governamental (ONG)",
    },
    {
      value: "Organização da Sociedade Civil de Interesse Público (OSCIP)",
      label: "Organização da Sociedade Civil de Interesse Público (OSCIP)",
    },
    {
      value: "Outro",
      label: "Outro",
    },
  ];

  useEffect(() => {
    if (watch().organization_type !== "Outro")
      setValue("other_organization_type", null);
  }, [watch().organization_type]);

  return (
    <>
      {realizationAddressModalOpen && (
        <ProjectModal
          title="Adicionar Locais de Realização"
          onSubmit={submitRealizationAddress}
          modalOpen={realizationAddressModalOpen}
          setModalOpen={setRealizationAddressModalOpen}
          showButtons={false}
        >
          <RealizationAddress
            currentUf={currentUf}
            setCurrentUf={setCurrentUf}
            setModalOpen={setRealizationAddressModalOpen}
            onSubmit={submitRealizationAddress}
          />
        </ProjectModal>
      )}

      <FormWrapper {...methods} submit={submit}>
        <S.Wrapper>
          <DefaultForm name="Dados - Organização/Instituição">
            <S.OrgGridForm>
              <Input
                {...register("responsible_name")}
                label="Nome do responsável pelo projeto:"
                placeholder="Digite aqui"
                gridArea="responsible_name"
                disabled={disabled}
              />
              <Input
                {...register("cep")}
                label="CEP:"
                placeholder="Digite aqui"
                gridArea="cep"
                mask="99999-999"
                disabled={disabled}
              />
              <Input
                {...register("state")}
                label="Estado:"
                placeholder="Digite aqui"
                gridArea="state"
                disabled={disabled}
              />
              <Input
                {...register("neighbourhood")}
                label="Bairro:"
                placeholder="Digite aqui"
                gridArea="neighbourhood"
                disabled={disabled}
              />
              <Input
                {...register("city")}
                label="Cidade:"
                placeholder="Digite aqui"
                gridArea="city"
                disabled={disabled}
              />
              <Input
                {...register("address")}
                label="Endereço Completo: "
                placeholder="Digite aqui"
                gridArea="address"
                disabled={disabled}
              />
              <Input
                {...register("email")}
                label="E-mail:"
                placeholder="Digite aqui"
                gridArea="email"
                disabled={disabled}
              />
              <Input
                {...register("phone")}
                label="Telefone:"
                placeholder="Digite aqui"
                mask="+99 (99) 99999-9999"
                gridArea="phone"
                disabled={disabled}
              />
              <Input
                {...register("role")}
                label="Cargo / Função:"
                placeholder="Digite aqui"
                gridArea="role"
                disabled={disabled}
              />
              <Select
                name="organization_type"
                label="Tipo de organização:"
                placeholder="Selecione"
                options={organizationTypeOptions}
                required
                maxWidth="90vw"
                disabled={disabled}
              />
              {watch().organization_type === "Outro" && (
                <Input
                  {...register("other_organization_type")}
                  label="Outro:"
                  placeholder="Digite aqui"
                  gridArea="other_organization_type"
                  disabled={disabled}
                />
              )}
              <Input
                {...register("organization_area")}
                label="Ramo de atividade da organização:"
                placeholder="Digite aqui"
                gridArea="organization_area"
                disabled={disabled}
              />
            </S.OrgGridForm>

            <Input
              {...register("institution_history")}
              label="Histórico da Instituição:"
              placeholder="Digite aqui"
              type="textarea"
              height="200px"
              disabled={disabled}
            />
          </DefaultForm>

          <DefaultForm name="Dados do Projeto">
            <S.DataGridForm>
              <Input
                {...register("project_name")}
                label="Nome do Projeto:"
                placeholder="Digite aqui"
                width="50%"
                disabled={disabled}
              />
              <Input
                {...register("project_summary")}
                label="Resumo do Projeto:"
                placeholder="Digite aqui"
                type="textarea"
                height="200px"
                disabled={disabled}
                maxLength={PROJECT_SUMMARY_MAX}
              />
              <CharCounter
                value={watch("project_summary")}
                max={PROJECT_SUMMARY_MAX}
              />

              <Select
                name="project_modality"
                label="Lei de Incentivo que o projeto está aprovado e/ou apto a captação:"
                placeholder="Digite aqui"
                options={categoryOptions}
                required
                isMulti
                value={selectedCategories}
                onChange={(e) => setSelectedCategories(e)}
                disabled={disabled}
                maxWidth="80vw"
              />

              <Input
                {...register("main_goal")}
                label="Objetivo Geral:"
                placeholder="Digite aqui"
                type="textarea"
                height="200px"
                disabled={disabled}
              />

              <DateInput
                start_label="Início de projeto:"
                finish_label="Conclusão de projeto:"
                start_name="project_start_at"
                finish_name="project_finish_at"
                disabled={disabled}
              />
            </S.DataGridForm>
          </DefaultForm>

          <DefaultForm name="Segmentação de Público">
            <Commitments
              commitments={commitments}
              setCommitments={setCommitments}
              disabled={disabled}
            />
            <Checkboxes
              label="Qual(is) a(s) faixa(s) etária(s) do(s) beneficiário(s) final(is) do projeto? "
              items={[
                { name: "childs", label: "Crianças" },
                { name: "adults", label: "Adulto" },
                { name: "teenagers", label: "Adolescentes" },
                { name: "elderly", label: "Idoso" },
                { name: "youngsters", label: "Jovens" },
                { name: "all", label: "Todos" },
              ]}
              selected={ageRange}
              setSelected={setAgeRange}
              disabled={disabled}
            />
            <S.DataGridForm>
              <Input
                {...register("project_direct_public_number")}
                placeholder="Digite aqui"
                label="Número total previsto de público direto impactado:"
                type="number"
                width="50%"
                disabled={disabled}
              />
            </S.DataGridForm>
          </DefaultForm>

          <DefaultForm name="Território">
            <S.LocalGridForm>
              {!isDownload && (
                <Button
                  type="button"
                  outline
                  text="Selecionar Locais de Realização"
                  onClick={() => setRealizationAddressModalOpen(true)}
                  width="90%"
                  disabled={disabled}
                />
              )}

              {Object.keys(realizationAddress).map((uf) => (
                <S.Uf key={uf}>
                  <h3>{uf}</h3>
                  <S.Cities>
                    {realizationAddress[uf].map((city) => (
                      <S.City key={city}>
                        {city}{" "}
                        {!disabled ? (
                          <GetIcon
                            name="X"
                            color={theme.colors.neutral.white}
                            onClick={() => {
                              const new_realization_address = {
                                ...realizationAddress,
                                [uf]: realizationAddress[uf].filter(
                                  (c) => c !== city,
                                ),
                              };

                              if (new_realization_address[uf].length === 0) {
                                delete new_realization_address[uf];
                              }

                              setRealizationAddress(new_realization_address);
                            }}
                          />
                        ) : null}
                      </S.City>
                    ))}
                  </S.Cities>
                </S.Uf>
              ))}
            </S.LocalGridForm>
          </DefaultForm>

          <DefaultForm name="Orçamento do Projeto">
            <S.DataGridForm>
              <Input
                {...register("total_authorized_value")}
                placeholder="Digite aqui"
                label="Valor total autorizado para captação (R$):"
                type="text"
                width="50%"
                value={totalAuthorizedValue ?? ""}
                onChange={(e) =>
                  setTotalAuthorizedValue(currencyMask(e.target.value))
                }
                disabled={disabled}
              />
              <Input
                {...register("capture_balance")}
                placeholder="Digite aqui"
                label="Saldo disponível a captar (R$):"
                type="text"
                width="50%"
                value={captureBalance ?? ""}
                onChange={(e) =>
                  setCaptureBalance(currencyMask(e.target.value))
                }
                disabled={disabled}
              />
              <Select
                name="related_areas"
                label="Área relacionada:"
                placeholder="Selecione"
                options={RELATED_AREA_OPTIONS}
                required
                isMulti
                value={selectedRelatedAreas}
                onChange={(e) => setSelectedRelatedAreas(e)}
                disabled={disabled}
                maxWidth="100%"
              />
            </S.DataGridForm>
          </DefaultForm>

          {!isDownload && (
            <S.Buttons>
              <Button type="submit" text="Próximo" />
            </S.Buttons>
          )}
        </S.Wrapper>
      </FormWrapper>
    </>
  );
};

export default FirstForm;
